import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  Briefcase,
  Cable,
  Gauge,
  GitBranch,
  Layers3,
  Radar,
  ShieldCheck,
  Workflow,
  Wrench,
} from 'lucide-react';
import ServiceLeadForm from './ServiceLeadForm';

const CONSULTATION_TYPES = [
  'Website Development',
  'Mobile App Development',
  'AI Automation',
  'AI Chat Bot Development',
];

const serviceCards = [
  {
    title: 'Technical Opportunity Strategy',
    icon: Bot,
    text: 'Turn unclear technical opportunities into clear, client-ready proposals with defined scope and delivery path.',
    outcome: 'Win confidence in high-value technical deals.',
  },
  {
    title: 'Scope + Specification Support',
    icon: Workflow,
    text: 'Work with an SME to structure requirements, architecture direction, and implementation assumptions.',
    outcome: 'Reduce proposal risk and rework.',
  },
  {
    title: 'Delivery Feasibility Planning',
    icon: Layers3,
    text: 'Validate timeline, team model, and execution dependencies before you commit to scope.',
    outcome: 'Protect margins while increasing win rate.',
  },
  {
    title: 'SME-Matched Consultation',
    icon: Cable,
    text: 'Get matched to the right specialist based on the exact project type and opportunity in front of you.',
    outcome: 'Right expert. Right call. Faster decisions.',
  },
];

const faqs = [
  {
    q: 'Is this advisory-only?',
    a: 'No. We focus on implementation and operational outcomes, not presentation-only consulting.',
  },
  {
    q: 'How quickly can we start?',
    a: 'Most teams can begin discovery immediately and move into execution planning within days.',
  },
  {
    q: 'Will this work with our current systems?',
    a: 'Yes. We optimize around your existing stack, then recommend upgrades where they drive leverage.',
  },
  {
    q: 'Can you support AI adoption across teams?',
    a: 'Yes. We build practical AI workflows and team adoption patterns tied to daily operations.',
  },
  {
    q: 'Is this built for scaling companies?',
    a: 'Yes. Engagements are built for growth-stage complexity, overloaded teams, and transition phases.',
  },
  {
    q: 'How hands-on is HireX8 during execution?',
    a: 'We stay embedded through rollout and optimization to ensure measurable operational gains.',
  },
];

const ctaHref = '#consultation-lead-form';

const ConsultationLanding = () => {
  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100">
      <Helmet>
        <title>Consultation | HireX8</title>
        <meta
          name="description"
          content="Scale smarter with AI-native operational consulting. Redesign workflows, deploy AI systems, and increase execution velocity without operational chaos."
        />
      </Helmet>

      <div className="sticky top-0 z-40 backdrop-blur bg-[#070b14]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center text-sm font-semibold text-orange-400 hover:opacity-80">
            ← Back to HireX8
          </Link>
          <a href={ctaHref} className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-orange-500 text-black hover:bg-orange-400 transition-colors">
            Book A Strategy Session <ArrowRight size={14} />
          </a>
        </div>
      </div>

      <section className="px-6 pt-14 sm:pt-20 pb-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">For Agency Owners + Project Managers</p>
            <h1 className="text-4xl sm:text-6xl font-black leading-[0.95] tracking-tight text-white">
              Win More Technical Engagements With The Right SME
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-2xl leading-relaxed">
              HireX8 helps digital agency owners and project managers who do not have the internal experience or
              resources to confidently propose and win technical opportunities.
            </p>
            <p className="mt-3 text-slate-400 font-medium">
              Request a consultation and get matched with an SME aligned to your specific project type and deal scenario.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a href={ctaHref} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-black font-semibold hover:bg-orange-400 transition-colors">
                Book A Strategy Session <ArrowRight size={16} />
              </a>
              <a href="#what-we-solve" className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/20 text-slate-100 font-semibold hover:bg-white/5 transition-colors">
                See What We Solve
              </a>
            </div>

            <div className="mt-7 flex flex-wrap gap-3 text-sm">
              {[
                'SME-matched advisory',
                'Proposal + scope support',
                'Technical opportunity planning',
                'Operator-led guidance',
                'Faster pre-sales decisions',
              ].map((m) => (
                <span key={m} className="px-3 py-1.5 rounded-full border border-white/15 text-slate-300 bg-white/5">
                  {m}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-slate-200">Operational Systems View</p>
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300">AI Active</span>
            </div>
            <div className="space-y-3">
              {[
                ['Opportunity Intake', 'Complete'],
                ['Project Type Matching', 'In Progress'],
                ['Scope + Risk Review', 'Queued'],
                ['Proposal Direction', 'Ready'],
              ].map(([item, status], idx) => (
                <div key={item} className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-200">{item}</span>
                    <span className="text-slate-400">{status}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full bg-orange-400"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${Math.min(34 + (idx + 1) * 16, 95)}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: idx * 0.12 }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { icon: Radar, label: 'Ops', sub: 'Visibility' },
                { icon: GitBranch, label: 'AI', sub: 'Orchestration' },
                { icon: Wrench, label: 'Exec', sub: 'Velocity' },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={sub} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
                  <Icon size={15} className="mx-auto text-orange-400 mb-1" />
                  <div className="font-bold text-white">{label}</div>
                  <div className="text-[11px] text-slate-400">{sub}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section id="what-we-solve" className="px-6 py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
            Most Agencies Don’t Lose On Talent. They Lose On Technical Confidence.
          </h2>
          <p className="mt-4 text-slate-300 max-w-3xl">
            When technical opportunities arrive, many agency owners and PMs lack the internal specialist depth to scope
            accurately, price confidently, and present a winning implementation path.
          </p>
          <div className="mt-7 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-6">
              <h3 className="font-bold text-red-200 mb-3">Before</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>Unclear technical scope</li>
                <li>Weak proposal confidence</li>
                <li>Lost or low-margin opportunities</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-6">
              <h3 className="font-bold text-emerald-200 mb-3">After</h3>
              <ul className="space-y-2 text-slate-200 text-sm">
                <li>SME-backed technical direction</li>
                <li>Stronger proposals and specs</li>
                <li>Higher win confidence</li>
              </ul>
            </div>
          </div>
          <div className="mt-6">
            <a href={ctaHref} className="inline-flex items-center gap-2 text-sm font-semibold text-orange-300 hover:text-orange-200 transition-colors">
              Get matched to the right SME <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Consultation Built To Help You Propose And Win</h2>
          <div className="mt-7 grid md:grid-cols-2 gap-5">
            {serviceCards.map(({ title, icon: Icon, text, outcome }) => (
              <motion.div
                key={title}
                whileHover={{ y: -4 }}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:border-orange-300/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center mb-4">
                  <Icon size={18} className="text-orange-300" />
                </div>
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <p className="mt-2 text-slate-300 text-sm leading-relaxed">{text}</p>
                <p className="mt-3 text-sm text-orange-300 font-semibold">{outcome}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Built By Operators. Not Generic Advisors.</h2>
          <div className="mt-7 overflow-hidden rounded-2xl border border-white/10">
            <div className="grid grid-cols-3 bg-white/[0.05] text-xs uppercase tracking-wider font-semibold">
              <div className="p-4 text-slate-300">Dimension</div>
              <div className="p-4 text-slate-400 border-l border-white/10">Traditional Consulting</div>
              <div className="p-4 text-orange-300 border-l border-white/10">HireX8</div>
            </div>
            {[
              ['Deal Readiness', 'General recommendations', 'Opportunity-specific guidance'],
              ['Technical Scope', 'High-level framing', 'SME-backed specification support'],
              ['Engagement Model', 'Advisory-only', 'Execution-aware strategy'],
              ['Response Speed', 'Slow turnaround', 'Fast decision support'],
              ['Outcome', 'Unclear next steps', 'Proposal-ready direction'],
            ].map((row) => (
              <div key={row[0]} className="grid grid-cols-3 border-t border-white/10 text-sm">
                <div className="p-4 text-slate-200">{row[0]}</div>
                <div className="p-4 text-slate-400 border-l border-white/10">{row[1]}</div>
                <div className="p-4 text-slate-100 border-l border-white/10">{row[2]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">From Opportunity Intake To Proposal Clarity</h2>
          <div className="mt-7 grid md:grid-cols-4 gap-4">
            {[
              'Share your active project opportunity',
              'We match you with the right SME',
              'Get scope, risk, and delivery guidance',
              'Submit a stronger proposal with confidence',
            ].map((step, idx) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="text-xs text-orange-300 font-semibold mb-2">Step {idx + 1}</div>
                <p className="text-slate-200 text-sm leading-relaxed">{step}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Trusted By Teams Competing For Technical Work</h2>
          <div className="mt-7 grid md:grid-cols-3 gap-4">
            {[
              'Increased proposal win confidence on technical deals',
              'Reduced pre-sales guesswork across delivery teams',
              'Improved scope accuracy before contracts are signed',
            ].map((m) => (
              <div key={m} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <Gauge size={18} className="text-orange-300 mb-3" />
                <p className="text-lg text-slate-100">{m}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm text-slate-300">
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">Logo rail placeholder</div>
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">Testimonial placeholder</div>
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">Case study placeholder</div>
          </div>
        </div>
      </section>

      <section className="px-6 py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Built For Agency Teams Selling Technical Engagements</h2>
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Digital agency owners',
              'Project managers leading complex deals',
              'Teams without in-house technical depth',
              'Agencies expanding into AI and automation work',
              'Firms bidding on engineering-heavy opportunities',
              'Teams that need proposal-ready SME support',
            ].map((audience) => (
              <div key={audience} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-center gap-2 text-slate-100 font-medium text-sm">
                  <Briefcase size={16} className="text-orange-300" />
                  {audience}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">FAQ</h2>
          <div className="mt-7 grid md:grid-cols-2 gap-4">
            {faqs.map((item) => (
              <details key={item.q} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 group">
                <summary className="cursor-pointer list-none flex items-center justify-between text-slate-100 font-semibold text-sm">
                  {item.q}
                  <ShieldCheck size={16} className="text-orange-300 group-open:rotate-6 transition-transform" />
                </summary>
                <p className="mt-3 text-slate-300 text-sm leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-14">
        <div className="max-w-7xl mx-auto rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:p-10">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white max-w-4xl">
            Stop Losing Technical Opportunities You Could Win
          </h2>
          <p className="mt-4 text-lg text-slate-300 max-w-3xl">
            HireX8 connects you to the right SME so you can scope accurately, propose confidently,
            and move technical engagements forward faster.
          </p>
          <p className="mt-2 text-slate-400">Less guesswork. Better proposals. Higher confidence.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={ctaHref} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-black font-semibold hover:bg-orange-400 transition-colors">
              Book A Strategy Session <ArrowRight size={16} />
            </a>
            <a href="#what-we-solve" className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/20 text-slate-100 font-semibold hover:bg-white/5 transition-colors">
              See What We Solve
            </a>
          </div>
        </div>
      </section>

      <section id="consultation-lead-form" className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          <ServiceLeadForm
            serviceName="Consultation"
            submitLabel="Book A Strategy Session"
            consultationOptions={CONSULTATION_TYPES}
            apiEndpoint="/api/send-lead"
            successRedirectPath="/consultation/what-to-expect"
          />
        </div>
      </section>

      <div className="sm:hidden fixed bottom-0 inset-x-0 z-50 p-3 bg-[#070b14]/95 border-t border-white/10 backdrop-blur">
        <a href={ctaHref} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-black font-semibold">
          Book A Strategy Session <ArrowRight size={16} />
        </a>
      </div>
    </div>
  );
};

export default ConsultationLanding;
